import { useEffect, useMemo, useRef, useState } from 'react';
import { Popover } from '~/components/ui/popover';
import { useApprovals } from '~/hooks/queries/use-approvals';
import { useProBuilderEnabled } from '~/hooks/queries/use-feature-flags';
import { useCodingEnabled } from '~/hooks/queries/use-tenant';
import { useProBuilderOpenStore } from '~/lib/stores/pro-builder-open-store';
import { ActivityBoard } from './activity-board';
import { AskChip } from './ask-chip';
import { BuildButton } from './build-button';
import { NewChatButton } from './new-chat-button';
import { StatusCluster } from './status-cluster';
import { askChipsFor, type AskChipInfo, type BarChat } from './state-mapping';
import { sessionToModalAsset } from '~/features/chat/modal-asset';
import { lastFreshBuildWindowId, minimizeIfShown } from '~/features/windows/window-payloads';
import { WindowTabs, placedTabs } from '~/features/windows/window-tabs';
import { useWindowStore } from '~/lib/stores/window-store';
import { needsChoice, type WindowEntry } from '~/lib/stores/window-rules';
import { buildWindowId, chatWindowId, PRO_BUILDER_WINDOW_ID } from '~/features/windows/window-titles';

/**
 * The question chips, and the window ids that are questions. A question stays
 * a chip even while its window is open (lit while shown) and the window tabs
 * skip it, so nothing moves between the two areas on a toggle (eddy,
 * 2026-10-26). Pure, tested directly (a string render cannot see the window
 * store's live state).
 */
export function topBarQuestions(
  barChats: BarChat[],
  approvals: Parameters<typeof askChipsFor>[1],
  openWindowIds: ReadonlySet<string>,
): { chips: AskChipInfo[]; more: number; askingWindowIds: Set<string> } {
  // A question keeps its chip whether or not its window is open — moving it
  // to the window tabs when it opened made the bar jump on every toggle
  // (eddy, 2026-10-26). The window tabs leave these ids out instead.
  void openWindowIds;
  const askingWindowIds = new Set(barChats.filter((c) => c.state === 'asking').map((c) => topBarWindowId(c)));
  return { ...askChipsFor(barChats, approvals), askingWindowIds };
}

/** One place in the bar after the + Chat / Build doors: a question chip or a window tab. */
export type BarSlot = { type: 'chip'; chip: AskChipInfo } | { type: 'tab'; id: string };

/**
 * ⌥1–⌥9 (eddy, 2026-10-26: "let me toggle each chat with option 1–9"): the
 * bar's chips and tabs, left to right, exactly as they are drawn — the chips
 * first, then the window tabs in `placedTabs` order. Pure, so the order the
 * keys follow is tested against the same function the tabs render from.
 */
export function barSlots(
  chips: readonly AskChipInfo[],
  windows: readonly WindowEntry[],
  askingWindowIds: ReadonlySet<string>,
): BarSlot[] {
  return [
    ...chips.map((chip) => ({ type: 'chip' as const, chip })),
    ...placedTabs(windows, askingWindowIds, false).map((w) => ({ type: 'tab' as const, id: w.id })),
  ];
}

/** ⌥1–⌥9 → slot 0–8, read off the PHYSICAL key (Option+digit types a symbol on a Mac). */
export function slotForKey(e: Pick<KeyboardEvent, 'altKey' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'code'>): number | null {
  if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return null;
  const m = /^Digit([1-9])$/.exec(e.code);
  return m ? Number(m[1]) - 1 : null;
}

/**
 * Inside a text field the shortcut still works — the window you are typing
 * in must hide on its own ⌥ number (eddy, 2026-10-26) — EXCEPT when the key
 * types a character people write with: on a German Mac keyboard Option+5…9
 * type [ ] | { }, and those must keep reaching the text. Option+1 types ¡,
 * which nobody means, so it toggles. Judged by what the key would TYPE
 * (`e.key`), never by the digit, so every keyboard layout gets the same rule.
 */
export function keyTypesText(e: Pick<KeyboardEvent, 'key'>): boolean {
  return /^[\x20-\x7e]$/.test(e.key);
}

/**
 * A second click on "+ Chat" / "Build" while a window of that kind waits for
 * the "which window to replace?" pick starts it minimized instead (eddy,
 * 2026-10-26). Returns whether it did.
 */
export function backgroundIfPending(kind: 'chat' | 'build'): boolean {
  const s = useWindowStore.getState();
  const pending = s.pendingId ? s.windows.find((w) => w.id === s.pendingId) : undefined;
  if (!pending || pending.kind !== kind) return false;
  s.backgroundPending();
  return true;
}

/** The window a top-bar chip opens — the same ids the window host uses. */
export function topBarWindowId(chip: Pick<AskChipInfo, 'kind' | 'sessionId'> & { title: string | null }): string {
  if (chip.kind === 'pro-builder') return PRO_BUILDER_WINDOW_ID;
  if (chip.kind === 'build') return buildWindowId(chip.sessionId);
  return chatWindowId(sessionToModalAsset(chip.sessionId, chip.title, new Date(0).toISOString()));
}
import { useChatActivity } from './use-chat-activity';
import { useOpenBuild } from './use-open-build';
import { useOptionHeld } from './slot-key';
import { useUiRefactoring } from '~/lib/ui-refactoring';
import { ChatTopBarLegacy } from './legacy/chat-top-bar-legacy';
import './top-bar.css';

interface ChatTopBarProps {
  /** Starts a brand-new chat — the header already owns this mechanism
   *  (blank asset + `requestOpenChat` + navigate home if elsewhere). */
  onNewChat: () => void;
  /** Opens an existing chat by session id, from any page. */
  onOpenChat: (sessionId: string, title: string | null) => void;
}

/**
 * The new chat-nav-in-the-top-bar surface (2026-08-14 new-chatbox-topbar
 * design doc): New chat button, status cluster (+ its activity board
 * dropdown), and the ask chip — the left group `app-header.tsx` renders
 * instead of the old plain New-chat button. It is unconditional now:
 * FRONT-1782 deleted the Chat design preference this used to be gated on
 * (and the classic layout it chose between), so the top bar is the only
 * chat design.
 *
 * Owns the board's open/closed state, the ⌘N shortcut, and the data
 * wiring (active chats + their statuses + pending approvals) that feeds the
 * pure `state-mapping` helpers. Opening a CHAT (where "open chat X" or
 * "start a new chat" actually goes) stays with the caller — `app-header.tsx`
 * already has the `useLocation`/`useChatOpenStore` wiring for that, and
 * reusing it here keeps there being exactly one such mechanism. FRONT-1681:
 * opening a BUILD (a Building Session row) goes through `useOpenBuild`
 * instead — that store, unlike the chat one, has no existing per-page wiring
 * in `app-header.tsx` to reuse. 2026-09-01: that hook is shared with the
 * classic design's header now, so both Build doorways navigate and hand
 * along their origin identically.
 */
export function ChatTopBar(props: ChatTopBarProps) {
  // FRONT-3223: `eddy.ui-refactoring` picks the bar. Off is the `origin/main`
  // bar (`legacy/`), never a greyed-out new one; the pick is read once per
  // render from the store the app shell seeded before anything mounted.
  return useUiRefactoring() ? <ChatTopBarV2 {...props} /> : <ChatTopBarLegacy {...props} />;
}

/** The reworked bar (FRONT-3144) — what `ChatTopBar` renders with `eddy.ui-refactoring` on. */
export function ChatTopBarV2({ onNewChat, onOpenChat }: ChatTopBarProps) {
  const [boardOpen, setBoardOpen] = useState(false);
  const openBuild = useOpenBuild();

  // FRONT-1579: the active-chats + statuses → BarChat[] → groups/counts
  // wiring now lives in `use-chat-activity.ts`, shared with the inbox chat
  // modal's `ChatActivityMenu` so the two boards can't drift apart.
  // FRONT-1681: `includeBuilds` is this surface's own opt-in — folds
  // Building Sessions into the same board, gated on `useCodingEnabled()` so
  // a deployment with coding switched off doesn't poll
  // `/api/coding/sessions` every 15s from this always-mounted header for
  // nothing. The in-modal `ChatActivityMenu` leaves it off entirely (its
  // own call site passes no options), so it stays byte-identical.
  const codingEnabled = useCodingEnabled();
  // FRONT-3040: the Pro Builder row shows for exactly the people the sidebar
  // shows its Pro Builder entry to (the same per-person flag, fallback off),
  // so nobody sees a row for a window they cannot open.
  const proBuilderEnabled = useProBuilderEnabled();
  const { barChats, groups, counts } = useChatActivity({
    includeBuilds: codingEnabled,
    includeProBuilder: proBuilderEnabled,
  });
  // FRONT-2307's browser-tab "(N)" count is published by the sidebar's
  // "Asking you" row (sidebar-asking.tsx) since FRONT-3143 — one writer, so
  // this bar unmounting can never zero a count the sidebar still shows.
  const { data: approvals = [] } = useApprovals();

  // Open windows are tabs in this bar (eddy, 2026-10-26). A question whose
  // window is already open is shown as that tab's amber dot, not as a second
  // chip — so the windows' ids decide which questions still need a chip.
  const windows = useWindowStore((s) => s.windows);
  const choosingReplace = useWindowStore((s) => needsChoice(s));
  // Which kind of window waits for the pick — a string, never a derived object.
  const pendingKind = useWindowStore((s) => (s.pendingId ? s.windows.find((w) => w.id === s.pendingId)?.kind : undefined));
  const openWindowIds = useMemo(
    () => new Set(windows.filter((w) => w.state !== 'pending').map((w) => w.id)),
    [windows],
  );
  // Each open window's chat / Build state, for its tab's status dot.
  const windowStates = useMemo(
    () => new Map(barChats.map((c) => [topBarWindowId(c), c.state] as const)),
    [barChats],
  );
  const shownWindowIds = useMemo(
    () => new Set(windows.filter((w) => w.state === 'visible').map((w) => w.id)),
    [windows],
  );
  // Every open question gets its own chip (eddy, 2026-10-26), not only the first.
  const { chips: askChips, more: moreAsking, askingWindowIds } = useMemo(
    () => topBarQuestions(barChats, approvals, openWindowIds),
    [barChats, approvals, openWindowIds],
  );

  // Global ⌥N / Alt+N — only live while this surface is mounted (i.e. the
  // topbar design is on). Note it has NO open-window guard, unlike its ⌥B
  // twin below, and that asymmetry is deliberate: starting a new chat with a
  // chat window open hands that same window a fresh thread, which is a
  // sensible thing to do, whereas ⌥B would paint a SECOND window on top of
  // the first. Same-looking badges, different rules — don't "fix" one to
  // match the other without reading both. Matched on e.code because on macOS Option+N types
  // a dead key and e.key is not "n"; the tradeoff is that e.code is the
  // PHYSICAL key, so on a non-QWERTY layout (Dvorak/Colemak) the combo sits
  // on whatever key is at the QWERTY-N position. Cmd/Ctrl+N is
  // browser-reserved and never reaches the page — see
  // NEW_CHAT_SHORTCUT_LABEL in new-chat-button.tsx, which must stay in sync
  // with this handler.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.code !== 'KeyN') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      // Same as a second click: a waiting new chat starts in the background.
      if (backgroundIfPending('chat')) return;
      onNewChat();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onNewChat]);

  // 2026-09-01: ⌥B / Alt+B — the Build button's twin of the handler above,
  // same physical-key and editable-target reasoning. Registered only while
  // building is switched on for the tenant, so the combo is inert exactly
  // where the button it mirrors is absent — and, like ⌥N, only while THIS
  // surface is mounted, so the classic design has neither shortcut (which is
  // why the classic header's own Build button carries no badge).
  useEffect(() => {
    if (!codingEnabled) return;
    function onKeyDown(e: KeyboardEvent) {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.code !== 'KeyB') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      // Never while a full-screen window is already open. The chat window and
      // the building window are both `role="dialog" aria-modal` overlays at
      // the same depth, and `home.tsx` mounts the building one AFTER the chat
      // one — so firing here would paint the second on top of a still-mounted
      // first, and Escape would then have two layers to unwind. FRONT-1081
      // solved the same collision for the composer's coding icon by closing
      // the chat first; that icon's handler is gone, and this listener has no
      // way to close a window it does not own, so it stands down instead. The
      // BUTTON needs no such guard: a full-screen overlay covers it.
      // Scope, precisely: Radix emits `role="dialog"` on Popover AND Dialog
      // content but `aria-modal` on NEITHER, so this catches exactly the
      // app's hand-rolled full-screen overlays — the chat window, the
      // building window, the cron form and the Pro Builder window (which
      // carries these attributes ONLY while visible) — and deliberately lets the
      // activity board's popover through. A shadcn Dialog or Sheet is NOT
      // covered: ⌥B with one of those open still opens the building window,
      // navigating home and unmounting the route it belonged to. That is a
      // lesser and different problem from the overlay stacking this exists
      // to stop, and widening the selector to bare `[role="dialog"]` would
      // kill the shortcut whenever the board is open, which is worse.
      // A waiting new build starts in the background, like a second click.
      if (backgroundIfPending('build')) {
        e.preventDefault();
        return;
      }
      if (document.querySelector('[role="dialog"][aria-modal]')) return;
      e.preventDefault();
      // Like the button: always a NEW build (eddy, 2026-10-26).
      openBuild(null, true);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [codingEnabled, openBuild]);

  function openFromBoard(sessionId: string, title: string | null) {
    setBoardOpen(false);
    onOpenChat(sessionId, title);
  }

  function openBuildFromBoard(sessionId: string, _title: string | null) {
    setBoardOpen(false);
    openBuild(sessionId);
  }

  // FRONT-3040: the window is mounted once in the app shell, so this works
  // from any page (the sidebar's entry does the same).
  function openProBuilderFromBoard() {
    setBoardOpen(false);
    useProBuilderOpenStore.getState().openProBuilder();
  }

  // A chip's click: open its window, or hide it when already shown. While a
  // third window waits, a shown question's chip picks it as the one to replace
  // (eddy, 2026-10-26: choose from the tabs). Shared with ⌥1–⌥9.
  function activateChip(chip: AskChipInfo) {
    const wid = topBarWindowId(chip);
    if (choosingReplace) {
      if (shownWindowIds.has(wid)) useWindowStore.getState().chooseReplace(wid);
      return;
    }
    if (minimizeIfShown(wid)) return;
    if (chip.kind === 'pro-builder') openProBuilderFromBoard();
    else if (chip.kind === 'build') openBuild(chip.sessionId);
    else onOpenChat(chip.sessionId, chip.title);
  }

  // ⌥1–⌥9 toggles the n-th chip or tab of the bar, left to right — the same
  // as clicking it (eddy, 2026-10-26). Off inside a text field: on a German
  // Mac keyboard Option+5…9 types [ ] | { }, which must keep working there.
  // Off while a new window waits for its place, so a key can't pick a window
  // to throw away.
  const slots = useMemo(() => barSlots(askChips, windows, askingWindowIds), [askChips, windows, askingWindowIds]);
  const activateChipRef = useRef(activateChip);
  activateChipRef.current = activateChip;
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const n = slotForKey(e);
      if (n === null) return;
      const t = e.target as HTMLElement | null;
      const inField = !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      if (inField && keyTypesText(e)) return;
      if (choosingReplace) return;
      const slot = slots[n];
      if (!slot) return;
      e.preventDefault();
      if (slot.type === 'chip') activateChipRef.current(slot.chip);
      else useWindowStore.getState().toggle(slot.id);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [slots, choosingReplace]);

  // While Option is held, every chip and tab shows its ⌥ number.
  const optionHeld = useOptionHeld();

  return (
    <div
      className="eddy-topbar group/topbar flex min-w-0 flex-1 items-center gap-[10px]"
      data-option-held={optionHeld && !choosingReplace ? 'true' : undefined}
    >
      <NewChatButton
        background={pendingKind === 'chat'}
        onClick={() => {
          // Clicked again while a new chat waits for the "which window?" pick:
          // start it in the background instead (eddy, 2026-10-26).
          if (backgroundIfPending('chat')) return;
          onNewChat();
        }}
      />
      {/* 2026-09-01: gated on the same `useCodingEnabled()` the board's build
       *  rows are — a deployment with building switched off must not show a
       *  door to a window it has no sessions for. */}
      {codingEnabled && (
        <BuildButton
          background={pendingKind === 'build'}
          onClick={() => {
            // Clicked again while a new build waits for the "which window?"
            // pick: start it in the background (eddy, 2026-10-26).
            if (backgroundIfPending('build')) return;
            // Build always starts a NEW build; a second click right after hides
            // the one it just opened (eddy, 2026-10-26).
            const last = lastFreshBuildWindowId();
            if (last && minimizeIfShown(last)) return;
            openBuild(null, true);
          }}
        />
      )}
      {/* Left: the doors (+ Chat, Build) and one chip per open question.
          Right: the status indicator and its activity board (eddy, 2026-10-26). */}
      <div data-testid="topbar-ask-chips" className="flex min-w-0 shrink items-center gap-[8px] overflow-hidden">
        {/* In the replace pick only chips whose window is shown remain — they are choices. */}
        {askChips.filter((chip) => !choosingReplace || shownWindowIds.has(topBarWindowId(chip))).map((chip, i) => (
          <AskChip
            slotKey={choosingReplace ? undefined : i + 1}
            key={`${chip.kind}:${chip.sessionId}`}
            label={chip.label}
            action={chip.action}
            active={shownWindowIds.has(topBarWindowId(chip))}
            replacing={choosingReplace && shownWindowIds.has(topBarWindowId(chip))}
            // FRONT-1681: a chip can be a Building Session — routed to
            // openBuild, never onOpenChat, or this would try to open a chat
            // that doesn't exist for that session id.
            // FRONT-3040: and the Pro Builder row opens its window.
            // A second click on the chip of a window that is already shown
            // hides it again (eddy, 2026-10-26).
            onClick={() => activateChip(chip)}
          />
        ))}
        {moreAsking > 0 && !choosingReplace && (
          <button
            type="button"
            onClick={() => setBoardOpen(true)}
            aria-label={`${moreAsking} more waiting on your answer`}
            className="inline-flex h-[38px] shrink-0 items-center rounded-[11px] px-[11px] text-[13px] font-medium text-amber-600 transition-colors hover:bg-[var(--bg-3)] dark:text-amber-400"
          >
            +{moreAsking}
          </button>
        )}
      </div>
      {/* Order left → right: + Chat, Build, questions, then the open chats
          (eddy, 2026-10-26); the status indicator stays on the far right. */}
      <WindowTabs exclude={askingWindowIds} states={windowStates} firstSlot={askChips.length + 1} />
      <span className="flex-1" aria-hidden />
      <Popover open={boardOpen} onOpenChange={setBoardOpen}>
        <StatusCluster counts={counts} />
        <ActivityBoard
          groups={groups}
          onOpen={openFromBoard}
          onOpenBuild={openBuildFromBoard}
          onOpenProBuilder={openProBuilderFromBoard}
        />
      </Popover>
    </div>
  );
}
